import { useMutation, useQueryClient } from '@tanstack/react-query'

import {
  companyInventoryClient,
  getGetCompanyDrawerMetadataQueryKey,
  getGetCompanyQueryKey,
  getListCompaniesDataTypesQueryKey,
} from '@/api/company.hook'
import { ControlWithSignals, fetchControlsExtended } from '@/api/control.hook'
import { ControlStatus } from '@/gen/controls/v1/control_status_pb'
import { BusinessOwner, Company, DataTypeInfo } from '@/gen/inventory/v1/company_service_pb'
import { GetControlResponse } from '@/gen/inventory/v1/control_service_pb'

import { controlStatusLabel, riskLevelLabel } from '@/const/label'

import { UrlParam } from '@/lib/url/url-param.hook'
import { buildAssessmentUrl, buildThirdPartyUrl } from '@/lib/url/urls'

import { ASSESSMENT_TABS } from '@/pages/assessment/assessment-tabs/assessment-tabs'
import {
  addBulletList,
  addHeading,
  addLinkParagraph,
  convertAdfToMarkdown,
} from '@/pages/assessment/components/create-jira-issue-modal/jira-adf-utils'

import { getInherentRisk } from '@/components/shared/risk-badge/utils'

export type IssueInfo = {
  summary: string
  descriptionText: string
  descriptionAdf: unknown[]
}

export type IssueInfoWithSubtasks = {
  parent: IssueInfo
  subtasks: IssueInfo[]
}

type UseJiraTicketProps = {
  companyId: string
  assessmentId: string
  controlIds: string[]
}

// Pulls data and generates the jira ticket content for a given company, assessment and controls/signals
// Note that it will generate a single issue with subtasks
export const useGenerateJiraTicketContent = () => {
  const queryClient = useQueryClient()

  return useMutation({
    mutationFn: async ({
      companyId,
      assessmentId,
      controlIds,
    }: UseJiraTicketProps): Promise<IssueInfoWithSubtasks> => {
      const [companyData, companyDrawerMetadataData, companiesDataTypes, controlsWithSignalsData] =
        await Promise.all([
          // GetCompany request
          queryClient.fetchQuery({
            queryKey: getGetCompanyQueryKey(companyId),
            queryFn: () => companyInventoryClient.getCompany({ id: companyId }),
          }),
          // GetCompanyDrawerMetadata for business owner
          queryClient.fetchQuery({
            queryKey: getGetCompanyDrawerMetadataQueryKey(companyId),
            queryFn: () =>
              companyInventoryClient.getCompanyDrawerMetadata({
                companyId: companyId,
              }),
          }),
          // ListCompaniesDataTypes for company datatypes from IRA
          queryClient.fetchQuery({
            queryKey: getListCompaniesDataTypesQueryKey(),
            queryFn: () => companyInventoryClient.listCompaniesDataTypes({}),
          }),
          // Controls + control signals
          fetchControlsExtended(controlIds),
        ])

      if (!companyData?.company) {
        throw new Error(`Company with id ${companyId} not found`)
      }

      let iraDatatypes: DataTypeInfo[] = []
      if (companyId in companiesDataTypes.companiesDataTypes) {
        iraDatatypes = companiesDataTypes.companiesDataTypes[companyId].dataTypes || []
      }

      const { descriptionAdf, descriptionText } = buildJiraParentDescription(
        assessmentId,
        companyData.company,
        iraDatatypes,
        companyDrawerMetadataData.businessOwner,
        controlsWithSignalsData,
      )

      const subtasks: IssueInfo[] = []
      for (const control of controlsWithSignalsData) {
        const { descriptionAdf, descriptionText } = buildJiraSubtaskDescriptionForControl(
          assessmentId,
          control,
        )

        subtasks.push({
          summary: `[${controlStatusLabel[control.control!.status]}]: ${control.control!.title}`,
          descriptionAdf,
          descriptionText,
        })
      }

      return {
        parent: {
          summary: `[Lema] ${companyData.company.profile?.name}`,
          descriptionText,
          descriptionAdf,
        },
        subtasks: subtasks,
      }
    },
  })
}

// buildJiraParentDescription builds a Jira description ADF (Atlassian Document Format) for an assessment
// see Jira's official documentation for more information https://developer.atlassian.com/cloud/jira/platform/atlassian-document-format/
function buildJiraParentDescription(
  assessmentId: string,
  company: Company,
  iraDatatypes: DataTypeInfo[],
  businessOwner?: BusinessOwner,
  controlsData?: GetControlResponse[],
) {
  const adfContent: unknown[] = []

  const assessmentUrl = buildAssessmentUrl(assessmentId, undefined, { absolute: true })
  const vendorUrl = buildThirdPartyUrl(company.id, undefined, { absolute: true })

  let businessOwnerText = 'N/A'
  if (businessOwner) {
    businessOwnerText = [businessOwner.name, businessOwner.email].join(' ')
  }
  const datascopeText = iraDatatypes.map((dt) => dt.dataType)?.join(', ') || 'N/A'

  // Vendor & business use case
  addHeading(adfContent, 2, 'Third party')
  addBulletList(adfContent, [
    `Name: ${company.profile?.name}`,
    `Description: ${company.profile?.longDescription}`,
    `Risk level: ${riskLevelLabel[getInherentRisk(company.validatedRisk, company.calculatedRisk)] || 'N/A'}`,
    `Business owner: ${businessOwnerText}`,
    `Data scope (from IRA): ${datascopeText}`,
  ])
  addLinkParagraph(adfContent, 'Third party in Lema', vendorUrl)
  addLinkParagraph(adfContent, 'Assessment in Lema', assessmentUrl)

  const controlCountByStatus = (controlsData ?? []).reduce(
    (acc, control) => {
      acc[control.control!.status] = (acc[control.control!.status] || 0) + 1
      return acc
    },
    {
      [ControlStatus.GAP]: 0,
      [ControlStatus.NO_EVIDENCE]: 0,
      [ControlStatus.UNSPECIFIED]: 0,
      [ControlStatus.VALIDATED]: 0,
    } as Record<ControlStatus, number>,
  )

  // Assessment summary
  addHeading(adfContent, 2, 'Assessment summary')
  addBulletList(adfContent, [
    `Controls: ${controlsData?.length}` +
      (controlsData && controlsData?.length > 0
        ? ` (${controlCountByStatus[ControlStatus.VALIDATED]} validated - ${controlCountByStatus[ControlStatus.GAP]} gaps - ${controlCountByStatus[ControlStatus.NO_EVIDENCE]} no evidence - ${controlCountByStatus[ControlStatus.UNSPECIFIED]} unspecified)`
        : ''),
  ])

  const descriptionText = convertAdfToMarkdown(adfContent)
  return { descriptionAdf: adfContent, descriptionText }
}

// buildJiraSubtaskDescription builds a Jira description ADF (Atlassian Document Format) for a control
// see Jira's official documentation for more information https://developer.atlassian.com/cloud/jira/platform/atlassian-document-format/
function buildJiraSubtaskDescriptionForControl(assessmentId: string, control: ControlWithSignals) {
  if (!control.control) {
    return {
      descriptionAdf: [],
      descriptionText: '',
    }
  }

  const adfContent: unknown[] = []

  const buildControlUrl = (id: string) =>
    buildAssessmentUrl(
      assessmentId,
      {
        [UrlParam.ASSESSMENT_TAB]: ASSESSMENT_TABS.CONTROLS,
        [UrlParam.CONTROL_ID]: id,
      },
      { absolute: true },
    )

  addBulletList(adfContent, [
    `Category: ${control.control.categoryName}`,
    `Description: ${control.controlDefinition}`,
    control.signals.length > 0
      ? {
          'Why is this a gap?': control.signals.map(
            (f) => `${f.signal?.title || ''}: ${f.signal?.subtitle || ''}`,
          ),
        }
      : `Why is this a gap?: ${control.analysisResult?.summary || 'N/A'}`,
  ])
  addLinkParagraph(adfContent, 'Control in Lema', buildControlUrl(control.control.id))

  const descriptionText = convertAdfToMarkdown(adfContent)
  return { descriptionAdf: adfContent, descriptionText }
}
